import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, Copy, CheckCircle2 } from 'lucide-react';
interface PageErrorBoundaryProps {
  children: React.ReactNode;
  pageName: string;
  onNavigate?: (page: string) => void;
}
interface PageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
  copied: boolean;
}
export class PageErrorBoundary extends Component<PageErrorBoundaryProps, PageErrorBoundaryState> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false
    };
  }
  static getDerivedStateFromError(error: Error): Partial<PageErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo
    });
    console.error(`[Page "${this.props.pageName}"] crashed:`, error, errorInfo);
  }
  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false
    });
  };
  handleGoHome = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      copied: false
    });
    this.props.onNavigate?.('home');
  };
  handleCopyError = () => {
    const {
      error,
      errorInfo
    } = this.state;
    const text = [`Page: ${this.props.pageName}`, `Error: ${error?.message}`, `Stack: ${error?.stack}`, `Component Stack: ${errorInfo?.componentStack}`].join('\n\n');
    navigator.clipboard.writeText(text).catch(() => {});
    this.setState({
      copied: true
    });
    setTimeout(() => this.setState({
      copied: false
    }), 2000);
  };
  render() {
    if (this.state.hasError) {
      return <div className="min-h-[60vh] flex items-center justify-center px-4 py-16" data-id="element-16448">
          <div className="w-full max-w-lg" data-id="element-16449">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-id="element-16450">
              {/* Header */}
              <div className="bg-gradient-to-r from-rose-50 via-amber-50 to-orange-50 px-6 py-6 border-b border-slate-100" data-id="element-16451">
                <div className="flex items-start gap-4" data-id="element-16452">
                  <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0" data-id="element-16453">
                    <AlertTriangle className="w-6 h-6 text-rose-500" data-id="element-16454" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-16455">
                    <h2 className="text-lg font-bold text-slate-900" data-id="element-16456">
                      Страница временно недоступна
                    </h2>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed" data-id="element-16457">
                      Произошла ошибка при загрузке. Остальные разделы сайта
                      работают нормально.
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4" data-id="element-16458">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-16459">
                  <div className="flex items-center gap-2 mb-2" data-id="element-16460">
                    <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-16461" />
                    <span className="text-xs font-bold text-amber-800 uppercase tracking-wider" data-id="element-16462">
                      Что можно сделать
                    </span>
                  </div>
                  <ul className="text-sm text-amber-900 space-y-1.5" data-id="element-16463">
                    <li className="flex items-start gap-2" data-id="element-16464">
                      <span className="text-amber-400 mt-0.5" data-id="element-16465">•</span>
                      Нажмите «Попробовать снова» — часто помогает
                    </li>
                    <li className="flex items-start gap-2" data-id="element-16466">
                      <span className="text-amber-400 mt-0.5" data-id="element-16467">•</span>
                      Вернитесь на главную и попробуйте позже
                    </li>
                    <li className="flex items-start gap-2" data-id="element-16468">
                      <span className="text-amber-400 mt-0.5" data-id="element-16469">•</span>
                      Обновите страницу в браузере (Ctrl+R)
                    </li>
                  </ul>
                </div>

                {/* Error details (collapsible) */}
                {this.state.error && <div data-id="element-16470">
                    <button onClick={() => this.setState({
                  showDetails: !this.state.showDetails
                })} className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-id="element-16471">
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${this.state.showDetails ? 'rotate-180' : ''}`} data-id="element-16472" />
                      Техническая информация
                    </button>
                    {this.state.showDetails && <div className="mt-2 relative" data-id="element-16473">
                        <pre className="p-3 bg-slate-900 rounded-lg text-[11px] text-slate-300 overflow-auto max-h-40 font-mono leading-relaxed" data-id="element-16474">
                          {this.state.error.message}
                          {this.state.errorInfo?.componentStack && <>
                              {'\n\nComponent Stack:'}
                              {this.state.errorInfo.componentStack}
                            </>}
                        </pre>
                        <button onClick={this.handleCopyError} className="absolute top-2 right-2 p-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors" title="Скопировать ошибку" data-id="element-16475">
                          {this.state.copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" data-id="element-16476" /> : <Copy className="w-3.5 h-3.5" data-id="element-16477" />}
                        </button>
                      </div>}
                  </div>}
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center gap-3" data-id="element-16478">
                <button onClick={this.handleRetry} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-16479">
                  <RefreshCw className="w-4 h-4" data-id="element-16480" />
                  Попробовать снова
                </button>
                <button onClick={this.handleGoHome} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-slate-600 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors" data-id="element-16481">
                  <Home className="w-4 h-4" data-id="element-16482" />
                  На главную
                </button>
              </div>
            </div>
          </div>
        </div>;
    }
    return this.props.children;
  }
}